// Remembers the last page the user was on and the last tab selected within a
// page, so re-opening the app lands where they left off instead of resetting.
import { useCallback, useState } from "react";

const LAST_ROUTE_KEY = "dt.ui.lastRoute";
const TAB_KEY_PREFIX = "dt.ui.tab.";
const RANGE_KEY_PREFIX = "dt.ui.range.";

export const ADMIN_HOME = "/main";
export const OPERATOR_HOME = "/lamination1";

type Role = "admin" | "manager" | "operator" | null;

// Managers reach every page an admin does, so they share the admin route set.
const hasAdminAccess = (role: Role) => role === "admin" || role === "manager";

// Only sidebar-reachable pages are remembered. Settings/account are utility
// screens reached from the top bar — landing there on open would be surprising.
const ADMIN_ROUTES = [
  "/main",
  "/sales-orders",
  "/manufacturing",
  "/inventory",
  "/lamination1",
  "/lamination2",
  "/foam",
  "/alerts",
  "/connectors",
];
const OPERATOR_ROUTES = ["/lamination1", "/lamination2", "/foam"];

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode / quota) — preferences are best-effort */
  }
};

export const homeRoute = (role: Role) =>
  hasAdminAccess(role) ? ADMIN_HOME : OPERATOR_HOME;

const isAllowed = (path: string, role: Role) =>
  (hasAdminAccess(role) ? ADMIN_ROUTES : OPERATOR_ROUTES).includes(path);

export const saveLastRoute = (path: string, role: Role) => {
  if (isAllowed(path, role)) write(LAST_ROUTE_KEY, path);
};

/** Last remembered page for this role, falling back to that role's home. */
export const loadLastRoute = (role: Role): string => {
  const saved = read(LAST_ROUTE_KEY);
  return saved && isAllowed(saved, role) ? saved : homeRoute(role);
};

export const clearUiPrefs = () => {
  try {
    localStorage.removeItem(LAST_ROUTE_KEY);
    Object.keys(localStorage)
      .filter(
        (k) => k.startsWith(TAB_KEY_PREFIX) || k.startsWith(RANGE_KEY_PREFIX)
      )
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* nothing to clear if storage is unavailable */
  }
};

/**
 * useState for a tab index that survives reloads.
 * `key` namespaces the page (e.g. "lam1"); `tabCount` guards against a stored
 * index that no longer exists after tabs are added/removed.
 */
export function useStickyTab(
  key: string,
  tabCount: number,
  defaultTab = 0
): [number, (value: number) => void] {
  const storageKey = TAB_KEY_PREFIX + key;

  const [tab, setTabState] = useState<number>(() => {
    const raw = read(storageKey);
    const n = raw === null ? NaN : Number(raw);
    return Number.isInteger(n) && n >= 0 && n < tabCount ? n : defaultTab;
  });

  const setTab = useCallback(
    (value: number) => {
      setTabState(value);
      write(storageKey, String(value));
    },
    [storageKey]
  );

  return [tab, setTab];
}

/**
 * useState for a quick-range preset, in hours, that survives reloads.
 *
 * `key` namespaces the chart (e.g. "extruders.lam1"); `allowed` guards a stored
 * value that is no longer one of the offered presets, so removing a button
 * cannot strand someone on a range they can no longer see selected.
 *
 * Only an explicit preset choice is remembered. Zooming into a marked band
 * leaves the stored preference alone, so a reload returns to the range the user
 * last actually picked rather than to a stale absolute window.
 */
export function useStickyQuickRange(
  key: string,
  allowed: readonly number[],
  defaultHours: number
): [number, (value: number) => void] {
  const storageKey = RANGE_KEY_PREFIX + key;

  const [hours, setHoursState] = useState<number>(() => {
    const raw = read(storageKey);
    const n = raw === null ? NaN : Number(raw);
    return allowed.includes(n) ? n : defaultHours;
  });

  const setHours = useCallback(
    (value: number) => {
      setHoursState(value);
      write(storageKey, String(value));
    },
    [storageKey]
  );

  return [hours, setHours];
}
